<template>
  <el-dialog
    :title="saveMode ? $tt('保存校验：页面数据异常') : $tt('检测到页面数据异常')"
    :visible.sync="dialogVisible"
    width="560px"
    append-to-body
    :close-on-click-modal="false"
    @closed="onClosed"
  >
    <p class="hint">
      {{
        saveMode
          ? $tt(
              "保存前需确认以下问题，否则下载清单或运行时可能与画布不一致。取消将中止本次保存。"
            )
          : $tt(
              "以下问题会导致画布显示、保存清单或运行时选中与数据不一致，请逐项确认处理方式。"
            )
      }}
    </p>
    <p v-if="pageName" class="page-name">{{ pageName }}</p>

    <div
      v-for="item in anomalies"
      :key="item.key"
      class="anomaly-block"
    >
      <div class="anomaly-title">{{ $tt(item.title) }}</div>
      <p class="anomaly-hint">{{ $tt(item.hint) }}</p>

      <template v-if="item.type === 'duplicate_back'">
        <el-radio-group
          v-model="localChoices[item.key]"
          class="choice-list"
        >
          <div
            v-for="c in item.candidates"
            :key="c.index"
            class="choice-item"
          >
            <el-radio :label="c.index">
              <span class="choice-label">
                {{ c.label }}
                <el-tag v-if="c.isCanvasVisible" size="mini" type="success">
                  {{ $tt("当前画布显示") }}
                </el-tag>
              </span>
            </el-radio>
            <div class="choice-preview">
              <img
                :src="previewSrc(c)"
                alt=""
                @error="onPreviewImgError(c.index)"
              />
            </div>
          </div>
        </el-radio-group>
      </template>

      <template v-else-if="item.type === 'duplicate_id'">
        <el-radio-group
          v-model="localChoices[item.key]"
          class="choice-list"
        >
          <div
            v-for="c in item.candidates"
            :key="c.index"
            class="choice-item compact"
          >
            <el-radio :label="c.index">
              <span class="choice-label">
                {{ c.label }}
                <span class="comp-type">（{{ c.component }}）</span>
              </span>
            </el-radio>
          </div>
        </el-radio-group>
      </template>

      <template v-else>
        <el-checkbox v-model="localChoices[item.key]">
          {{ $tt("按建议修复") }}
        </el-checkbox>
      </template>
    </div>

    <span slot="footer" class="dialog-footer">
      <el-button size="small" @click="onCancel">{{ $tt("取 消") }}</el-button>
      <el-button size="small" type="primary" @click="onConfirm">
        {{ $tt("确 定") }}
      </el-button>
    </span>
  </el-dialog>
</template>

<script>
import { buildDefaultAnomalyChoices } from "@/utils/canvasPageIntegrity";
import {
  BACK_PREVIEW_PLACEHOLDER,
} from "@/utils/canvasBackDuplicate";

export default {
  name: "CanvasPageIntegrityDialog",
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    pageName: {
      type: String,
      default: "",
    },
    anomalies: {
      type: Array,
      default: () => [],
    },
    saveMode: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      localChoices: {},
      previewFailed: {},
    };
  },
  computed: {
    dialogVisible: {
      get() {
        return this.visible;
      },
      set(v) {
        this.$emit("update:visible", v);
      },
    },
  },
  watch: {
    visible(v) {
      if (v) {
        this.previewFailed = {};
        this.localChoices = {
          ...buildDefaultAnomalyChoices(this.anomalies),
        };
        this.anomalies.forEach((a) => {
          if (
            a.type === "missing_back" ||
            a.type === "back_not_first"
          ) {
            if (this.localChoices[a.key] === undefined) {
              this.$set(this.localChoices, a.key, true);
            }
          }
        });
      }
    },
  },
  methods: {
    previewSrc(c) {
      if (this.previewFailed[c.index]) {
        return BACK_PREVIEW_PLACEHOLDER;
      }
      const url = c && c.previewUrl;
      if (url) {
        return url;
      }
      return BACK_PREVIEW_PLACEHOLDER;
    },
    onPreviewImgError(index) {
      this.$set(this.previewFailed, index, true);
    },
    onConfirm() {
      this.$emit("confirm", { ...this.localChoices });
      this.dialogVisible = false;
    },
    onCancel() {
      this.$emit("cancel");
      this.dialogVisible = false;
    },
    onClosed() {
      this.$emit("closed");
    },
  },
};
</script>

<style lang="scss" scoped>
.hint {
  margin: 0 0 8px;
  line-height: 1.5;
  color: #606266;
  font-size: 13px;
}
.page-name {
  margin: 0 0 12px;
  font-weight: 600;
  color: #303133;
}
.anomaly-block {
  margin-bottom: 16px;
  padding: 12px;
  border: 1px solid #ebeef5;
  border-radius: 6px;
  background: #fafafa;
}
.anomaly-title {
  font-weight: 600;
  color: #303133;
  margin-bottom: 6px;
}
.anomaly-hint {
  margin: 0 0 10px;
  font-size: 12px;
  line-height: 1.5;
  color: #909399;
}
.choice-list {
  display: block;
  width: 100%;
}
.choice-item {
  margin-bottom: 12px;
  padding: 8px;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  background: #fff;
  &.compact {
    margin-bottom: 8px;
  }
}
.choice-label {
  vertical-align: middle;
}
.comp-type {
  color: #909399;
  font-size: 12px;
}
.choice-preview {
  margin-top: 8px;
  margin-left: 24px;
  min-height: 100px;
  max-height: 160px;
  overflow: hidden;
  border-radius: 4px;
  background: #f5f7fa;
  border: 1px solid #ebeef5;
  display: flex;
  align-items: center;
  justify-content: center;
  img {
    max-width: 100%;
    max-height: 160px;
    display: block;
    object-fit: contain;
  }
}
</style>
